// ./src/components/features/Cars/MultiCarSelector/MultiCarSelector.tsx
import React, { useMemo, useCallback } from "react";
import {
  MultiSelect,
  MultiSelectProps,
  Group,
  Text,
  Stack,
  Box,
  ComboboxProps,
} from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Car } from "@/utils/api";
import { useDashboardContext } from "@/contexts";
import { useCars } from "@/hooks";
import { LoadingIndicator, AppIcon } from "@/components/generic";
import { getCarDisplayName } from "@/utils/carHelpers";

// ============================================
// Types
// ============================================

export interface MultiCarSelectorProps
  extends Omit<MultiSelectProps, "data" | "value" | "onChange" | "name"> {
  /** Field name for form binding */
  name?: string;

  /** Currently selected car IDs, or null for "All Vehicles" */
  value?: string[] | null;

  /** Callback fired when car selection changes: (name, value, cars?) */
  onChange?: (name: string, value: string[] | null, cars?: Car[]) => void;

  /** Placeholder text when no car is selected */
  placeholder?: string;

  /**
   * Whether to include inactive cars in the list.
   * When false (default), uses active cars from DashboardContext (no extra fetch).
   * When true, loads all cars via a separate useCars hook.
   */
  includeInactive?: boolean;

  /** Custom filter function to filter cars */
  carFilter?: (car: Car) => boolean;

  /** Whether the field is required (at least one selection) */
  required?: boolean;

  /** Placeholder text shown while loading */
  loadingPlaceholder?: string;

  /** Text shown when no cars are available */
  emptyText?: string;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Whether to show car details (make/model) in dropdown */
  showDetails?: boolean;

  /** Label for the field */
  label?: string;

  /** Whether to show "All Vehicles" option */
  showAllOption?: boolean;

  /** Label for "All Vehicles" option */
  allOptionLabel?: string;

  /** Props passed to the underlying Combobox (e.g., withinPortal) */
  comboboxProps?: ComboboxProps;

  /** Whether to render dropdown in portal */
  withinPortal?: boolean;
}

// ============================================
// Option Data Interface
// ============================================

interface CarOptionData {
  value: string;
  label: string;
  details?: string;
  disabled?: boolean;
}

// ============================================
// Constants
// ============================================

const ALL_VEHICLES_VALUE = "__all__";

// ============================================
// Component
// ============================================

const MultiCarSelectorComponent: React.FC<MultiCarSelectorProps> = ({
  name = "carIds",
  value,
  onChange,
  placeholder,
  includeInactive = false,
  carFilter,
  required = false,
  loadingPlaceholder,
  emptyText,
  error,
  disabled,
  size = "md",
  showDetails = true,
  label,
  showAllOption = false,
  allOptionLabel,
  comboboxProps,
  withinPortal,
  ...multiSelectProps
}) => {
  const { t } = useTranslation(["refuels", "common"]);

  // Active cars from dashboard context (already loaded, no extra fetch)
  const { cars: activeCars, loading: dashboardLoading, getCarById: getDashboardCarById } = useDashboardContext();

  // All-status cars hook — only fetches when includeInactive is true
  const {
    cars: allCars,
    loading: allCarsLoading,
    getCarById: getAllCarById,
    lastError: allCarsError,
  } = useCars({
    autoLoad: includeInactive,
    initialFilters: includeInactive ? {} : { status: [] },
  });

  // Resolve which car list and loading state to use
  const availableCars = includeInactive ? allCars : activeCars;
  const loading = includeInactive ? allCarsLoading : dashboardLoading;
  const lastError = includeInactive ? allCarsError : null;

  // Unified car lookup (check both sources)
  const getCarById = useCallback(
    (id: string | undefined): Car | undefined => {
      if (!id) return undefined;
      return getDashboardCarById(id) ?? getAllCarById(id);
    },
    [getDashboardCarById, getAllCarById]
  );

  // Default translations
  const defaultPlaceholder =
    placeholder || t("refuels:selectors.car.placeholderMulti", t("refuels:selectors.car.placeholder"));
  const defaultLabel = label;
  const defaultLoadingPlaceholder =
    loadingPlaceholder || t("refuels:selectors.car.loading");
  const defaultEmptyText = emptyText || t("refuels:selectors.car.empty");
  const defaultAllOptionLabel = allOptionLabel || t("refuels:list.allVehicles");

  // Create a map for quick car lookup by ID
  const carMap = useMemo(() => {
    const map = new Map<string, Car>();
    availableCars.forEach((car) => {
      if (car.id) {
        map.set(car.id, car);
      }
    });
    return map;
  }, [availableCars]);

  // Filter and prepare car options
  const carOptions = useMemo((): CarOptionData[] => {
    let filteredCars = availableCars;

    // Apply custom filter if provided
    if (carFilter) {
      filteredCars = filteredCars.filter(carFilter);
    }

    // Build options array
    const options: CarOptionData[] = [];

    // Add "All Vehicles" option if enabled
    if (showAllOption) {
      options.push({
        value: ALL_VEHICLES_VALUE,
        label: defaultAllOptionLabel,
        details: undefined,
      });
    }

    // Add car options
    filteredCars.forEach((car) => {
      if (car.id) {
        options.push({
          value: car.id,
          label: getCarDisplayName(car) || car.label || "Unnamed Vehicle",
          details: showDetails
            ? [car.vehicleMake?.makeName, car.model].filter(Boolean).join(" ") ||
            undefined
            : undefined,
        });
      }
    });

    return options;
  }, [
    availableCars,
    carFilter,
    showAllOption,
    defaultAllOptionLabel,
    showDetails,
  ]);

  // Convert value to display value (handle null = all vehicles)
  const displayValue = useMemo((): string[] => {
    if (value === null && showAllOption) {
      return [ALL_VEHICLES_VALUE];
    }
    return value || [];
  }, [value, showAllOption]);

  // Handle onChange with car objects lookup
  const handleChange = useCallback(
    (selectedValues: string[]) => {
      // Check if "All Vehicles" was just selected
      const hadAllSelected = displayValue.includes(ALL_VEHICLES_VALUE);
      const hasAllSelected = selectedValues.includes(ALL_VEHICLES_VALUE);

      if (!hadAllSelected && hasAllSelected) {
        // User selected "All Vehicles" - clear individual selections and return null
        onChange?.(name, null, undefined);
        return;
      }

      if (hadAllSelected && selectedValues.length > 1) {
        // User had "All Vehicles" and selected individual cars - remove "All"
        const filteredValues = selectedValues.filter(
          (v) => v !== ALL_VEHICLES_VALUE
        );
        const selectedCars = filteredValues
          .map((id) => carMap.get(id) || getCarById(id))
          .filter((car): car is Car => car !== undefined);
        onChange?.(name, filteredValues, selectedCars);
        return;
      }

      if (hasAllSelected && selectedValues.length === 1) {
        // Only "All Vehicles" selected - return null
        onChange?.(name, null, undefined);
        return;
      }

      // Regular selection of individual cars
      if (selectedValues.length === 0) {
        onChange?.(name, [], []);
      } else {
        const filteredValues = selectedValues.filter(
          (v) => v !== ALL_VEHICLES_VALUE
        );
        const selectedCars = filteredValues
          .map((id) => carMap.get(id) || getCarById(id))
          .filter((car): car is Car => car !== undefined);
        onChange?.(name, filteredValues, selectedCars);
      }
    },
    [name, displayValue, carMap, getCarById, onChange]
  );

  // Render option for dropdown
  const renderOption: MultiSelectProps["renderOption"] = useCallback(
    ({ option }: { option: CarOptionData }) => {
      // Special rendering for "All Vehicles"
      if (option.value === ALL_VEHICLES_VALUE) {
        return (
          <Group gap="sm" wrap="nowrap">
            <AppIcon iconName="Car" size={24} />
            <Text size="md" fw={500}>
              {option.label}
            </Text>
          </Group>
        );
      }

      return (
        <Group gap="sm" wrap="nowrap" align="flex-start">
          <Box pt={2}>
            <AppIcon iconName="Car" size={24} />
          </Box>
          <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
            <Text size="md" fw={500} lineClamp={1}>
              {option.label}
            </Text>
            {option.details && (
              <Text size="sm" c="dimmed" lineClamp={1}>
                {option.details}
              </Text>
            )}
          </Stack>
        </Group>
      );
    },
    []
  );

  // Memoize left section icon
  const leftSection = useMemo(() => {
    return <AppIcon iconName="Car" size={24} />;
  }, []);

  // Determine current state
  const isEmpty = !loading && availableCars.length === 0;

  // Memoize select state
  const selectState = useMemo(() => {
    if (loading && availableCars.length === 0) {
      return {
        data: [] as CarOptionData[],
        placeholder: defaultLoadingPlaceholder,
        isDisabled: true,
        showLoading: true,
        errorMessage: undefined as string | undefined,
      };
    }

    if (lastError && !loading && availableCars.length === 0) {
      return {
        data: [] as CarOptionData[],
        placeholder: t("common:errors.generic"),
        isDisabled: true,
        showLoading: false,
        errorMessage: error || t("common:messages.loadError"),
      };
    }

    if (isEmpty && !showAllOption) {
      return {
        data: [] as CarOptionData[],
        placeholder: defaultEmptyText,
        isDisabled: true,
        showLoading: false,
        errorMessage: undefined as string | undefined,
      };
    }

    return {
      data: carOptions,
      placeholder: defaultPlaceholder,
      isDisabled: disabled || loading,
      showLoading: loading && availableCars.length > 0,
      errorMessage: undefined as string | undefined,
    };
  }, [
    loading,
    availableCars.length,
    lastError,
    isEmpty,
    showAllOption,
    carOptions,
    defaultLoadingPlaceholder,
    defaultEmptyText,
    defaultPlaceholder,
    disabled,
    error,
    t,
  ]);

  // Memoize right section
  const rightSection = useMemo(() => {
    if (selectState.showLoading) {
      return <LoadingIndicator size={size} color="brand" />;
    }
    return multiSelectProps.rightSection;
  }, [selectState.showLoading, size, multiSelectProps.rightSection]);

  // Combobox props for portal rendering
  const mergedComboboxProps = useMemo(() => {
    const portalProps = withinPortal
      ? { withinPortal: true, zIndex: 1000 }
      : {};
    return {
      ...(comboboxProps || {}),
      ...portalProps,
    };
  }, [comboboxProps, withinPortal]);

  return (
    <Box>
      <MultiSelect
        {...multiSelectProps}
        label={defaultLabel}
        size={size}
        value={displayValue}
        onChange={handleChange}
        placeholder={selectState.placeholder}
        clearable={!required}
        required={required}
        disabled={selectState.isDisabled}
        error={selectState.errorMessage || error}
        data={selectState.data}
        rightSection={rightSection}
        leftSection={leftSection}
        renderOption={renderOption}
        hidePickedOptions={false}
        nothingFoundMessage={t("common:form.noOptions")}
        comboboxProps={mergedComboboxProps}
      />
    </Box>
  );
};

export const MultiCarSelector = React.memo(MultiCarSelectorComponent);